/**
 * MONSTER TOWN — BUILD CREW SHARED STATE. OWNED BY THE BUILD CREW (src/build/**).
 *
 * What is selected, which way it is turned, whether the hammer is in place or
 * delete mode, and which piece (if any) is currently being carried by a finger.
 * Kept in its own module so ghost / gestures / palette / placer can all read it
 * without importing each other in a knot.
 *
 * KID RULES ENCODED HERE:
 *  - There is always a selection once build mode is entered (`ensureSelection`),
 *    so tapping the ground can never silently do nothing.
 *  - Rotation is a single button, four states, wraps forever. No dead end.
 *  - Delete is a MODE you can see, not a gesture you can trigger by accident.
 */

import { bus } from '../core/events'
import { getPlaceable } from '../core/placeable'
import { nextRot, type Cell, type Rot } from '../core/grid'
import type { PaletteName } from '../core/palette'
import type { InstanceId, PlaceableId, SceneId } from '../core/types'
import type { BuildTool } from './events'
import './events'

// ---------------------------------------------------------------------------
// selection + rotation
// ---------------------------------------------------------------------------

let selected: PlaceableId | null = null
let ghostRot: Rot = 0
let tool: BuildTool = 'place'

export function selectedPlaceable(): PlaceableId | null {
  return selected
}

/**
 * Arm a piece for placing. `null` disarms.
 *
 * An id that is not registered is REFUSED and the previous good selection is
 * kept. This is not pedantry: arming an unknown id used to poison build mode
 * outright — `landingFor()` calls `requirePlaceable()`, which throws, so the
 * ghost stopped drawing and EVERY subsequent tap on the ground threw inside the
 * bus handler and placed nothing, with no visible sign anything was wrong. The
 * bus swallowing the error is what made it silent. This file's own rule is
 * "tapping the ground can never silently do nothing"; enforce it here, at the
 * one door every crew's selection comes through.
 */
export function selectPlaceable(id: PlaceableId | null): void {
  if (id !== null && getPlaceable(id) === undefined) {
    console.warn(`[build] ignoring selection of unknown placeable "${id}"`)
    return
  }
  if (selected === id) return
  selected = id
  ghostRot = 0
  bus.emit('build:selected', { defId: id })
}

/** Used by the HUD's hammer so build mode always has something to place. */
export function ensureSelection(fallback: PlaceableId): void {
  if (selected === null) selectPlaceable(fallback)
}

export function selectionRot(): Rot {
  return ghostRot
}

export function rotateSelection(): void {
  ghostRot = nextRot(ghostRot)
  bus.emit('build:rotated', { rot: ghostRot })
}

export function setSelectionRot(rot: Rot): void {
  ghostRot = rot
}

// ---------------------------------------------------------------------------
// tool (place / delete)
// ---------------------------------------------------------------------------

export function buildTool(): BuildTool {
  return tool
}

export function setBuildTool(next: BuildTool): void {
  if (next === tool) return
  const previous = tool
  tool = next
  bus.emit('build:toolChanged', { tool: next, previous })
}

export function toggleDeleteTool(): void {
  setBuildTool(tool === 'delete' ? 'place' : 'delete')
}

// ---------------------------------------------------------------------------
// carry (tap-hold to pick up, drag to slide)
// ---------------------------------------------------------------------------

/**
 * A piece a finger has picked up. NOTE: it is never removed from the grid while
 * carried — only its art is hidden and the ghost shows where it would go. That
 * way a carried piece cannot be lost by a dropped touch, a mode change, a scene
 * change or a reload: the worst case is it stays exactly where it was (LAW #2).
 */
export interface Carry {
  instanceId: InstanceId
  defId: PlaceableId
  sceneId: SceneId
  from: Cell
  fromRot: Rot
}

let carry: Carry | null = null

export function carried(): Carry | null {
  return carry
}

export function setCarry(c: Carry | null): void {
  carry = c
}

/** The def the ghost should be showing right now: the carried piece wins. */
export function ghostDefId(): PlaceableId | null {
  return carry?.defId ?? selected
}

// ---------------------------------------------------------------------------
// tints (kid recolours). Keyed per scene+instance, persisted by the placer.
// ---------------------------------------------------------------------------

export const tints = new Map<string, PaletteName>()

export function tintKey(sceneId: SceneId, id: InstanceId): string {
  return `${sceneId}#${id}`
}
